import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import AssetListRow from './AssetListRow';
import { Loader } from '../../App/components/Loader';
import * as AssetActions from '../actions/assets';

const AssetList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { username, userId, authHydrated, assetList, loading } = useSelector(
    (state) => ({
      username: state.user.username,
      userId: state.user.id,
      authHydrated: state.user.authHydrated,
      assetList: state.assets.list,
      loading: state.loading
    })
  );

  // Fetch only once Firebase auth is CONFIRMED ready (authHydrated). The optimistic cache makes
  // `userId` available at mount, but authorized Storage/Firestore reads fail until auth.currentUser
  // exists — so gating on userId alone would fire getAssets too early and get an empty result.
  useEffect(() => {
    if (userId && authHydrated) {
      dispatch(AssetActions.getAssets());
    }
  }, [userId, authHydrated, dispatch]);

  // Treat the pre-hydration window as loading so we show the spinner, not a misleading empty state.
  const resolving = !authHydrated || loading;
  const hasAssets = () => !resolving && assetList.length > 0;

  const renderLoader = () => (resolving ? <Loader /> : null);

  const renderEmptyTable = () => {
    if (!resolving && assetList.length === 0) {
      return (
        <p className="asset-table__empty">{t('AssetList.NoUploadedAssets')}</p>
      );
    }
    return null;
  };

  return (
    <article className="asset-table-container">
      <Helmet>
        <title>{t('AssetList.Title')}</title>
      </Helmet>
      {renderLoader()}
      {renderEmptyTable()}
      {hasAssets() && (
        <table className="asset-table">
          <thead>
            <tr>
              <th>{t('AssetList.HeaderName')}</th>
              <th>{t('AssetList.HeaderSize')}</th>
              <th>{t('AssetList.HeaderSketch')}</th>
              <th aria-label="dropdown" scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {assetList.map((asset) => (
              <AssetListRow asset={asset} key={asset.key} username={username} />
            ))}
          </tbody>
        </table>
      )}
    </article>
  );
};

export default AssetList;
